import { getHiveflowConfig, isNativeBridgeAllowed } from "@/lib/environment";

export const NATIVE_RESULT_TYPES = new Set([
  "SCAN_RESULT",
  "LOCATION_RESULT",
  "OFFLINE_STATUS",
  "OFFLINE_STATUS_RESULT",
  "SYNC_RESULT",
  "SYNC_STATUS",
  "SYNC_NOTICE",
  "OFFLINE_CACHE_READY",
  "OFFLINE_DATA_RESULT",
  "OFFLINE_COLLECTION_RESULT",
  "OFFLINE_COMMAND_QUEUED",
  "OFFLINE_DATA",
  "GET_OFFLINE_DATA_RESULT",
  "INSPECTION_OFFLINE_SAVED",
  "SAVE_INSPECTION_OFFLINE_RESULT",
  "INSPECTION_OFFLINE_RESULT",
  "OFFLINE_ACTION_SAVED",
  "OFFLINE_CONFLICT",
  "OFFLINE_CONFLICTS_RESULT",
  "SYNC_CONFLICT",
  "UPDATE_HIVE_STATUS_OFFLINE_RESULT",
  "SAVE_QR_ACTION_OFFLINE_RESULT",
  "RESOLVE_OFFLINE_CONFLICT_RESULT",
  "APP_CAPABILITIES",
  "AUTH_SESSION_RESULT",
  "STAND_DETECTION_STATUS_RESULT",
  "STAND_CONTEXT_ACTIVE",
  "STAND_CONTEXT_CHANGED",
  "STAND_CONTEXT_ENTERED",
  "STAND_CONTEXT_RESULT",
  "STAND_CONTEXT_SUGGESTION",
  "STAND_CONTEXT_UNAVAILABLE",
  "STAND_CONTEXT_EXITED",
  "NATIVE_ERROR",
]);

export const NATIVE_REQUEST_TYPES = new Set([
  "REQUEST_MOBILE_AUTH",
]);

export function isReactNativeWebView() {
  return (
    typeof window !== "undefined" &&
    !!window.ReactNativeWebView &&
    typeof window.ReactNativeWebView.postMessage === "function"
  );
}

export function isNativeApp() {
  return isReactNativeWebView() && isNativeBridgeAllowed();
}

export function createNativeRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function sendToNative(type, payload = {}) {
  if (!isReactNativeWebView()) return false;
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({
      type,
      payload: { requestId: createNativeRequestId(), ...(payload || {}) },
    }));
    return true;
  } catch {
    return false;
  }
}

export function openScanner(payload = {}) {
  return sendToNative("OPEN_SCANNER", payload);
}

export function getLocation(payload = {}) {
  return sendToNative("GET_LOCATION", payload);
}

export function saveOfflineAction(type, payload = {}) {
  return sendToNative("SAVE_OFFLINE_ACTION", { actionType: type, payload: payload || {} });
}

export function getOfflineData(payload = {}) {
  return sendToNative("GET_OFFLINE_DATA", payload);
}

export function getOfflineCollection(payload = {}) {
  return sendToNative("GET_OFFLINE_COLLECTION", payload);
}

export function queueOfflineCommand(payload = {}) {
  return sendToNative("QUEUE_OFFLINE_COMMAND", payload);
}

export function saveInspectionOffline(payload = {}) {
  return sendToNative("SAVE_INSPECTION_OFFLINE", payload);
}

export function updateHiveStatusOffline(payload = {}) {
  return sendToNative("UPDATE_HIVE_STATUS_OFFLINE", payload);
}

export function saveQrActionOffline(payload = {}) {
  return sendToNative("SAVE_QR_ACTION_OFFLINE", payload);
}

export function resolveOfflineConflict(payload = {}) {
  return sendToNative("RESOLVE_OFFLINE_CONFLICT", payload);
}

export function getSyncStatus(payload = {}) {
  return sendToNative("GET_SYNC_STATUS", payload);
}

export function startSync(payload = {}) {
  return sendToNative("START_SYNC", payload);
}

export function syncNow(payload = {}) {
  return sendToNative("SYNC_NOW", payload);
}

export function getOfflineStatus(payload = {}) {
  return sendToNative("GET_OFFLINE_STATUS", payload);
}

export function getAppCapabilities(payload = {}) {
  return sendToNative("GET_APP_CAPABILITIES", payload);
}

export function enableStandDetection(payload = {}) {
  return sendToNative("ENABLE_STAND_DETECTION", payload);
}

export function getStandDetectionStatus(payload = {}) {
  return sendToNative("GET_STAND_DETECTION_STATUS", payload);
}

export function getStandContext(payload = {}) {
  return sendToNative("GET_STAND_CONTEXT", payload);
}

export function refreshStandContext(payload = {}) {
  return sendToNative("REFRESH_STAND_CONTEXT", payload);
}

export function notifyWebBridgeReady(payload = {}) {
  return sendToNative("WEB_BRIDGE_READY", { bridgeVersion: 2, syncProtocolVersion: 2, ...payload });
}

export function setStandOverride(standId) {
  return sendToNative("SET_STAND_OVERRIDE", { standId });
}

export function setStandAutoApply(enabled) {
  return sendToNative("SET_STAND_AUTO_APPLY", { enabled: Boolean(enabled) });
}

export function clearStandOverride(payload = {}) {
  return sendToNative("CLEAR_STAND_OVERRIDE", payload);
}

export function requestAuthSession(payload = {}) {
  return sendToNative("GET_AUTH_SESSION", payload);
}

export function storeAuthSession(payload = {}) {
  return sendToNative("AUTH_SESSION_UPDATED", payload);
}

export function clearAuthSession(payload = {}) {
  return sendToNative("AUTH_SESSION_CLEARED", payload);
}

export function buildNativeAppUrl(path, params = {}) {
  const scheme = (process.env.REACT_APP_NATIVE_APP_SCHEME || "hiveflowmobile").replace(/:\/?\/?$/, "");
  const cleanPath = String(path || "").replace(/^\/+/, "");
  const query = new URLSearchParams(params).toString();
  return `${scheme}://${cleanPath}${query ? `?${query}` : ""}`;
}

export function openNativeAppRoute(path, params = {}) {
  if (typeof window === "undefined") return false;
  window.location.assign(buildNativeAppUrl(path, params));
  return true;
}

export function navigateAppRoute(path, { replace = false } = {}) {
  if (typeof window === "undefined") return false;
  const target = `/${String(path || "").replace(/^\/+/, "")}`;
  if (getHiveflowConfig().offlineBundle || window.location.protocol === "file:") {
    const hashTarget = `#${target}`;
    if (replace) window.location.replace(hashTarget);
    else window.location.hash = hashTarget;
    return true;
  }
  if (replace) window.location.replace(target);
  else window.location.assign(target);
  return true;
}

export function normalizeNativeResult(event) {
  let detail = event?.detail ?? event?.data ?? event;
  if (typeof detail === "string") {
    try { detail = JSON.parse(detail); } catch { detail = { type: "NATIVE_ERROR", payload: { message: "Ongeldig native bericht" } }; }
  }
  const type = detail?.type || detail?.eventType || "NATIVE_ERROR";
  const payload = detail?.payload ?? detail?.data ?? (detail && typeof detail === "object"
    ? Object.fromEntries(Object.entries(detail).filter(([key]) => key !== "type" && key !== "eventType"))
    : {});
  return {
    type,
    payload,
    eventId: detail?.eventId ?? detail?.event_id ?? payload?.eventId ?? payload?.event_id ?? null,
    eventVersion: detail?.eventVersion ?? detail?.event_version ?? payload?.eventVersion ?? payload?.event_version ?? null,
    timestamp: detail?.timestamp ?? detail?.sentAt ?? detail?.sent_at ?? payload?.timestamp ?? payload?.sentAt ?? payload?.sent_at ?? null,
    requestId: detail?.requestId ?? detail?.request_id ?? payload?.requestId ?? payload?.request_id ?? null,
  };
}

export function normalizeNativeRequest(event) {
  let detail = event?.detail ?? event?.data ?? event;
  if (typeof detail === "string") {
    try { detail = JSON.parse(detail); } catch { detail = { type: "NATIVE_ERROR", payload: { message: "Ongeldig native bericht" } }; }
  }
  const type = detail?.type || detail?.eventType || "NATIVE_ERROR";
  const payload = detail?.payload ?? detail?.data ?? {};
  return { type, payload };
}

export function addNativeResultListener(handler) {
  if (typeof window === "undefined") return () => {};
  const wrapped = (event) => {
    const result = normalizeNativeResult(event);
    if (NATIVE_RESULT_TYPES.has(result.type)) handler(result);
  };
  window.addEventListener("message", wrapped);
  document.addEventListener("message", wrapped);
  window.addEventListener("hiveflow:native-result", wrapped);
  return () => {
    window.removeEventListener("message", wrapped);
    document.removeEventListener("message", wrapped);
    window.removeEventListener("hiveflow:native-result", wrapped);
  };
}

export function addNativeRequestListener(handler) {
  if (typeof window === "undefined" || !isNativeBridgeAllowed()) return () => {};
  const wrapped = (event) => {
    const request = normalizeNativeRequest(event);
    if (NATIVE_REQUEST_TYPES.has(request.type)) handler(request);
  };
  window.addEventListener("message", wrapped);
  document.addEventListener("message", wrapped);
  window.addEventListener("hiveflow:native-request", wrapped);
  return () => {
    window.removeEventListener("message", wrapped);
    document.removeEventListener("message", wrapped);
    window.removeEventListener("hiveflow:native-request", wrapped);
  };
}
